import {
  Box,
  Button,
  Card,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { WrappedEpisodeOfCare } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/EpisodeOfCare';
import { ItemBox } from '../pages/EpisodeOfCareDetails';
import { Group } from '@mui/icons-material';
import { i18nFunction } from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { useCareTeams } from 'src/careflow/ph-core/fhir-client/fhir-react';
import {
  CareTeamWrapper,
  WrappedCareTeam,
} from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/CareTeam';
import { useEffect, useMemo, useState } from 'react';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import { getReferenceId } from 'src/utils/fhir';
import CareTeamGrid, {
  CareTeamFilters,
  initialCareTeamFilters,
} from '../../case/care-team-management/CareTeamGrid/CareTeamGrid';
import useGridFilters from 'src/careflow/ph-core/grid/hook/useGridFilters';
import CancelButton from 'src/careflow/common/CancelButton';
import { checkAclValidation } from 'src/careflow/ph-core/admin/permissions/util/permission.utils';
import crsAcls from 'src/careflow/ph-core/admin/permissions/util/crs/crsAcls';
import { PermissionsProvider } from 'src/careflow/ph-core/admin/permissions/context/PermissionsContext';
import { Reference } from 'src/careflow/ph-core/fhir-client/resources';
import Iconify from 'src/components/Iconify';
import { debounce } from 'src/utils/timers';
import { filterCareTeams } from '../../case/detail/components/CaseCareTeamGrid';

function CareTeam({
  eoc,
  patient,
  handle,
  i18n,
}: {
  eoc: WrappedEpisodeOfCare | null;
  patient?: WrappedPatient | null;
  handle: Function;
  i18n: i18nFunction;
}) {
  const [filteredCareTeams, setFilteredCareTeams] = useState<WrappedCareTeam[]>([]);

  const [careTeams, { refresh }] = useCareTeams({
    filter: { subject: patient?.id },
    map: CareTeamWrapper,
    autofetch: !!patient,
  });

  const [openModal, setOpenModal] = useState(false);

  const matched = useMemo(() => {
    const eocCareTeamReferenceId = getReferenceId(eoc?.team?.[0]?.reference);
    const matched = filteredCareTeams?.find((careTeam) => careTeam?.id === eocCareTeamReferenceId);
    return matched;
  }, [eoc?.team, filteredCareTeams]);

  const save = async (data: any) => {
    const payload = {
      ...eoc,
      team: [data],
    };
    await handle(payload);
  };

  const { filters, searchTextFieldValue, ...gridFiltersProps } =
    useGridFilters<CareTeamFilters>(initialCareTeamFilters);

  const handleFiltersChange = useMemo(
    () =>
      debounce((careTeams, filters, searchTextFieldValue) => {
        setFilteredCareTeams(filterCareTeams(careTeams, { filters, searchTextFieldValue }));
      }, 800),
    []
  );

  useEffect(() => {
    handleFiltersChange(careTeams, filters, searchTextFieldValue);
  }, [filters, searchTextFieldValue, careTeams, handleFiltersChange]);

  const careTeamPermissions = useMemo(
    () => ({
      isAllowedToAdd: checkAclValidation({
        acls: [crsAcls.CRS.EPISODE_OF_CARE.CARE_TEAMS.ADD],
      }),
      members: {
        isAllowedToAdd: checkAclValidation({
          acls: [crsAcls.CRS.EPISODE_OF_CARE.CARE_TEAMS.MEMBERS.ADD],
        }),
      },
    }),
    []
  );

  const managingOrganizations = useMemo(
    () =>
      careTeams?.reduce<Reference[]>(
        (allManagingOrganizations, careTeam) => [
          ...allManagingOrganizations,
          ...(careTeam?.managingOrganization?.reduce<Reference[]>((managingOrganizations, mg) => {
            if (
              managingOrganizations.find(({ reference }) => reference === mg.reference) ||
              allManagingOrganizations.find(({ reference }) => reference === mg.reference)
            )
              return managingOrganizations;
            return [...managingOrganizations, mg];
          }, []) ?? []),
        ],
        []
      ),
    [careTeams]
  );

  const actions = (careTeam: WrappedCareTeam) => {
    const team = careTeam?.id === matched?.id;
    return (
      <IconButton
        onClick={() => {
          save({ reference: `CareTeam/${careTeam.id}` });
          setOpenModal(false);
        }}
        disabled={team}
        color={team ? 'success' : 'info'}
      >
        {team ? <Iconify icon={'eva:checkmark-fill'} /> : <Iconify icon={'eva:plus-fill'} />}
      </IconButton>
    );
  };

  return (
    <>
      <ItemBox sx={{ borderRadius: 2, mt: 2 }}>
        <Typography
          sx={{
            fontSize: '10px',
            textTransform: 'uppercase',
            color: 'text.secondary',
            fontWeight: 500,
            letterSpacing: '0.08em',
          }}
          variant="caption"
        >
          {i18n('episodeOfCare.details.careTeam.careTeam', 'crs')}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            py: 1,
          }}
        >
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: (theme) => theme.palette.info.main + '30',
              display: 'flex',
            }}
          >
            <Group color="info" fontSize="large" />
          </Box>

          <Box>
            <Typography fontWeight="bold">{matched?.name}</Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
              }}
            >
              {matched?.category?.[0]?.text ?? matched?.category?.[0]?.coding?.[0]?.display}
            </Typography>
          </Box>
        </Box>
        <Button color="primary" variant="contained" onClick={() => setOpenModal(true)}>
          {i18n('episodeOfCare.details.careTeam.addCareTeam', 'crs')}
        </Button>
      </ItemBox>
      <Dialog open={openModal} fullWidth={true} maxWidth="lg">
        <DialogTitle>{i18n('episodeOfCare.details.careTeam.title', 'crs')}</DialogTitle>
        <Card sx={{ m: 2 }}>
          <Stack sx={{ m: 2 }}>
            <PermissionsProvider permissions={careTeamPermissions}>
              <CareTeamGrid
                searchTextFieldValue={searchTextFieldValue}
                carePlan={{} as any}
                patient={patient as any}
                managingOrganizations={managingOrganizations}
                onMemberUpdateSuccess={refresh}
                onSuccessfulCreation={refresh}
                onSuccessfulEdit={refresh}
                careTeams={filteredCareTeams}
                filterValues={filters}
                showAddButton={careTeamPermissions?.isAllowedToAdd}
                actions={actions}
                showFilters={false}
                {...gridFiltersProps}
              />
            </PermissionsProvider>
          </Stack>
          <DialogActions>
            <CancelButton onClick={() => setOpenModal(false)}>{i18n('cancel')}</CancelButton>
          </DialogActions>
        </Card>
      </Dialog>
    </>
  );
}

export default CareTeam;
